/* eslint-disable no-restricted-syntax -- amber token for warning, theme has no warning colour */
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import type { Connection } from '@utils/types/connections/connection.ts';
import { getPlatformSpec } from '@utils/types/connections/platforms.ts';
import type {
  PublishHistoryEntry,
  PublishPayload,
  PublishTarget
} from '@utils/types/publishing/publish.ts';

const WARNING_ICON = '#B45309';
const WARNING_BORDER = '#F59E0B55';

interface Props {
  connection: Connection;
  target: PublishTarget;
  agendaItemTitle?: string;
  payload: PublishPayload;
  /** The matching prior-publish entry, if any. */
  priorAttempt?: PublishHistoryEntry;
}

export default function StepConfirm({
  connection,
  target,
  agendaItemTitle,
  payload,
  priorAttempt
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const spec = getPlatformSpec(connection.platform);
  const isNewVideo = target.kind === 'youtube_new_video';

  return (
    <div className="flex flex-col gap-3">
      <div
        className="flex flex-col gap-2 rounded-xl border p-4"
        style={{
          borderColor: getColour('hairline'),
          background: getColour('secondLayer')
        }}
      >
        <SummaryRow
          label={t('publish-confirm-platform')}
          value={spec?.displayName ?? connection.platform}
        />
        <SummaryRow
          label={t('publish-confirm-account')}
          value={connection.account_name}
        />
        <SummaryRow
          label={t('publish-confirm-target')}
          value={
            isNewVideo
              ? t('publish-confirm-target-new-video', { title: target.title })
              : target.title
          }
        />
        {agendaItemTitle && (
          <SummaryRow
            label={t('publish-confirm-agenda-item')}
            value={agendaItemTitle}
          />
        )}
        <SummaryRow
          label={t('publish-confirm-sending')}
          value={
            payload.kind === 'captions'
              ? t('publish-confirm-captions', {
                  languages: payload.languages
                    .map(c => c.toUpperCase())
                    .join(', ')
                })
              : t('publish-confirm-transcript', {
                  format: payload.format.toUpperCase()
                })
          }
        />
        {isNewVideo && (
          <SummaryRow
            label={t('publish-confirm-privacy')}
            value={t(`publish-confirm-privacy-${target.privacy ?? 'private'}`)}
          />
        )}
      </div>

      {priorAttempt && !isNewVideo && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-xl border p-4"
          style={{
            borderColor: WARNING_BORDER,
            background: getColour('thirdLayer')
          }}
        >
          <AlertTriangle
            className="h-5 w-5 shrink-0"
            aria-hidden="true"
            style={{ color: WARNING_ICON }}
          />
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold">
              {t('publish-confirm-replace-title')}
            </p>
            <p className="text-sm" style={{ color: getColour('normalText') }}>
              {t('publish-confirm-replace-body', {
                target: target.title,
                date: priorAttempt.published_at ?? ''
              })}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  const { getColour } = useThemeContext();
  return (
    <div className="flex items-start gap-3">
      <span
        className="min-w-[130px] text-xs uppercase tracking-wide"
        style={{ color: getColour('inactiveText') }}
      >
        {label}
      </span>
      <span className="text-sm" style={{ color: getColour('normalText') }}>
        {value}
      </span>
    </div>
  );
}
